import { useRef, useEffect, useCallback } from 'react';

export type BoidEntity = {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  type: 'fish' | 'predator';
  flock: number;
};
export type FoodEntity = { id: number; x: number; y: number };
export type Wall = [number, number, number, number];
export type SimState = { boids: BoidEntity[]; food: FoodEntity[]; walls: Wall[] };

const WS_URL = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`;

export function useSimulation() {
  const stateRef = useRef<SimState | null>(null);
  const sessionRef = useRef<string | null>(null);

  useEffect(() => {
    let ws: WebSocket;
    let dead = false;

    const connect = () => {
      ws = new WebSocket(WS_URL);
      ws.onmessage = (e) => {
        const data = JSON.parse(e.data);
        if ('sessionId' in data) {
          sessionRef.current = data.sessionId;
          const { sessionId: _, ...simState } = data;
          stateRef.current = simState as SimState;
        } else {
          stateRef.current = data as SimState;
        }
      };
      ws.onerror = (e) => console.error('WebSocket error', e);
      ws.onclose = () => {
        if (!dead) setTimeout(connect, 2000);
      };
    };

    connect();
    return () => {
      dead = true;
      ws.close();
    };
  }, []);

  const post = useCallback(
    (endpoint: string, body: object) => {
      const sid = sessionRef.current;
      if (!sid) return Promise.resolve(new Response());
      return fetch(`/session/${sid}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    },
    []
  );

  const postFish = useCallback((x: number, y: number, flock: number = 0) => post('fish', { x, y, flock }), [post]);
  const postFood = useCallback((x: number, y: number) => post('food', { x, y }), [post]);
  const postPredator = useCallback((x: number, y: number) => post('predator', { x, y }), [post]);
  const postConfig = useCallback((config: object) => post('config', config), [post]);
  const postWall = useCallback((x1: number, y1: number, x2: number, y2: number) => post('wall', { x1, y1, x2, y2 }), [post]);
  const postSpeed = useCallback((speed: number) => post('speed', { speed }), [post]);
  const postErase = useCallback((x: number, y: number, radius: number) => post('erase', { x, y, radius }), [post]);
  const postPause = useCallback(() => post('pause', {}), [post]);

  return { stateRef, postFish, postFood, postPredator, postConfig, postWall, postSpeed, postErase, postPause };
}
